<template>
    <modal :isLoading="isLoading" @close="closeModal">
        <h1 class="modal__title">
            <small>{{ trans('page.services.cancel-modal.title') }}</small>
        </h1>
        <div class="alert alert--danger | u-margin-top-flush u-margin-bottom-narrow">
            <span
                class="u-text-break-word"
                v-html="trans('page.services.cancel-modal.warning-text', { domain: this.domain })"
            />
        </div>

        <form class="u-margin-bottom-narrow" @submit.prevent="submitServiceCancelForm">
            <div v-for="(subscription, key) in subscriptions" :key="subscription.uuid">
                <div class="u-margin-bottom-narrow">
                    <div class="u-flex-center">
                        <div class="field field--inline field--cancel">
                            <input
                                v-model="serviceCancelForm.subscriptions[key]['cancel']"
                                class="field__input | u-margin-right-inline"
                                :class="{ 'u-visually-hidden': subscription.administrative_status === 'canceled' }"
                                :id="createFieldId('cancel', subscription.uuid)"
                                type="checkbox"
                            />
                            <label
                                class="field__label"
                                :class="{ 'field--disabled': subscription.administrative_status === 'canceled' }"
                                :for="createFieldId('cancel', subscription.uuid)"
                            >
                                <strong>{{ trans('page.services.cancel-modal.type.' + subscription.type) }} </strong>
                                {{ subscription.name }}
                            </label>
                        </div>
                        <small v-if="!serviceCancelForm.subscriptions[key]['cancel_immediately']">
                            <span v-if="subscription.administrative_status === 'active'">
                                {{ trans('page.services.cancel-modal.cancel-by') }}
                            </span>
                            <span v-else>
                                {{ trans('page.services.cancel-modal.cancel-on') }}
                            </span>
                            <strong>{{ subscription.end_date | date }}</strong>
                        </small>
                    </div>
                    <div v-if="subscription.type === 'hosting'" class="field field--inline field--cancel">
                        <input
                            v-model="serviceCancelForm.subscriptions[key]['cancel_immediately']"
                            class="field__input | u-margin-right-inline"
                            :id="createFieldId('cancel_immediately', subscription.uuid)"
                            type="checkbox"
                        />
                        <label class="field__label" :for="createFieldId('cancel_immediately', subscription.uuid)">
                            <small v-html="trans('page.services.cancel-modal.cancel-immediately')">
                            </small>
                        </label>
                    </div>
                    <div v-if="subscription.type === 'extension'" class="field field--inline field--cancel">
                        <input
                            v-model="serviceCancelForm.subscriptions[key]['transfer']"
                            class="field__input | u-margin-right-inline"
                            :id="createFieldId('transfer', subscription.uuid)"
                            type="checkbox"
                        />
                        <label class="field__label" :for="createFieldId('transfer', subscription.uuid)">
                            <small>{{ trans('page.services.cancel-modal.transfer') }}</small>
                        </label>
                    </div>
                </div>
            </div>

            <div class="modal__nav">
                <button
                    class="btn btn--ghost-gray btn-ghost--dns | u-margin-right"
                    type="button"
                    :disabled="isLoading"
                    @click="closeModal"
                >
                    {{ trans('action.cancel') }}
                </button>
                <button
                    class="btn btn--blue-record btn-ghost--dns"
                    dusk="confirm-button"
                    type="submit"
                    :disabled="!canSubmit"
                >
                    {{ trans('action.discontinue') }}
                </button>
            </div>
        </form>
    </modal>
</template>

<script>
import { apiMixin } from 'relapp-api';
import { Modal } from 'relapp-modal';
import { Way2Form } from 'relapp-way2forms';
import { ServicesApi } from 'relapp-technical-management';

export default {
    components: {
        Modal
    },
    mixins: [apiMixin],
    props: {
        domain: {
            type: String,
            required: true
        },
        subscriptions: {
            type: Array,
            default: null
        }
    },
    /**
     * Add all the properties found in the data object to Vue’s reactivity system.
     *
     * @return {object}
     */
    data() {
        return {
            serviceCancelForm: new Way2Form({
                subscriptions: this.subscriptions.map(subscription => {
                    return {
                        type: subscription.type,
                        uuid: subscription.uuid,
                        cancel: false,
                        cancel_immediately: false,
                        transfer: false
                    };
                })
            }),
            isLoading: false
        };
    },
    computed: {
        /**
         * Check if form can be submitted.
         *
         * @returns {boolean}
         */
        canSubmit() {
            return (
                !this.isLoading &&
                this.serviceCancelForm.subscriptions.some(
                    subscription => subscription.cancel || subscription.cancel_immediately || subscription.transfer
                )
            );
        }
    },
    methods: {
        /**
         * Creates a field id from a prefix and uuid.
         *
         * @param {string} prefix
         * @param {string} uuid
         *
         * @returns {string}
         */
        createFieldId(prefix, uuid) {
            return prefix + '-' + uuid;
        },
        /**
         * Close the modal.
         */
        closeModal() {
            this.$emit('close');
        },
        /**
         * Submit the service cancel form.
         */
        submitServiceCancelForm() {
            this.isLoading = true;
            ServicesApi.cancel({ form: this.serviceCancelForm })
                .then(() => {
                    this.closeModal();
                    this.serviceCancelForm.reset();
                })
                .catch(this.handleError)
                .finally((this.isLoading = false));
        }
    }
};
</script>
